import { useEffect, useMemo, useState } from "react"
import { ArrowLeft, ChevronRight, Clock3, Pencil } from "lucide-react"
import EditStartTimeModal from "./EditStartTimeModal"

interface ClockScreenProps {
  jobName: string
  hourlyRate: number
  isActive: boolean
  startTime: string | null
  isOnBreak: boolean
  totalBreakSeconds: number
  onBack: () => void
  onOpenHistory: () => void
  onClockIn: () => void
  onClockInCustom: () => void
  onPause: () => void
  onResume: () => void
  onClockOut: () => void
  onEditStartTime: (newStartIso: string) => Promise<void> | void
}

const pad = (n: number) => String(n).padStart(2, "0")

const formatElapsed = (seconds: number) => {
  const s = Math.max(0, seconds)
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  const sec = s % 60
  return h > 0 ? `${h}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`
}

const formatMoney = (v: number) => `$${v.toFixed(2)}`

export default function ClockScreen({
  jobName,
  hourlyRate,
  isActive,
  startTime,
  isOnBreak,
  totalBreakSeconds,
  onBack,
  onOpenHistory,
  onClockIn,
  onClockInCustom,
  onPause,
  onResume,
  onClockOut,
  onEditStartTime,
}: ClockScreenProps) {
  const startMs = useMemo(() => (startTime ? new Date(startTime).getTime() : 0), [startTime])
  const [nowMs, setNowMs] = useState(Date.now())
  const [editStartOpen, setEditStartOpen] = useState(false)

  useEffect(() => {
    if (!isActive || isOnBreak) return
    const t = setInterval(() => setNowMs(Date.now()), 1000)
    return () => clearInterval(t)
  }, [isActive, isOnBreak])

  const workedSeconds = useMemo(() => {
    if (!isActive) return 0
    const raw = Math.floor((nowMs - startMs) / 1000)
    return Math.max(0, raw - totalBreakSeconds)
  }, [isActive, nowMs, startMs, totalBreakSeconds])

  const elapsedLabel = useMemo(() => formatElapsed(workedSeconds), [workedSeconds])
  const liveEarnings = useMemo(() => (workedSeconds / 3600) * hourlyRate, [workedSeconds, hourlyRate])

  return (
    <div className="mx-auto max-w-md min-h-[calc(100dvh-56px)] flex flex-col px-4 pb-32 text-black dark:text-white">
      <div className="flex items-center py-4">
        <button
          onClick={onBack}
          className="p-2 -ml-2 rounded-xl text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-neutral-800 active:bg-gray-100 dark:active:bg-neutral-800 transition"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
      </div>

      <button
        onClick={onOpenHistory}
        className="flex items-center justify-between gap-2 mb-10 px-3 py-3 -mx-3 rounded-xl text-left hover:bg-gray-50 dark:hover:bg-neutral-900 active:bg-gray-100 dark:active:bg-neutral-800 transition"
      >
        <div className="min-w-0">
          <p className="text-xl font-semibold truncate">{jobName}</p>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            ${hourlyRate.toFixed(2)}/hr · View history
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 shrink-0" />
      </button>

      <div className="flex-1 flex flex-col items-center justify-center text-center">
        <div className="flex items-center gap-1.5 mb-3">
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
            {isActive ? (isOnBreak ? "On break" : "Elapsed") : "Ready to clock in"}
          </p>
          {isActive && (
            <button
              onClick={() => setEditStartOpen(true)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-neutral-800 transition"
              title="Edit clock-in time"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        <p className="text-6xl font-bold tabular-nums mb-4">
          {isActive ? elapsedLabel : "00:00"}
        </p>
        {isActive && (
          <p className="text-lg font-semibold text-[#2563EB]">{formatMoney(liveEarnings)}</p>
        )}
      </div>

      {isActive && startTime && (
        <EditStartTimeModal
          open={editStartOpen}
          startTime={startTime}
          onClose={() => setEditStartOpen(false)}
          onSave={onEditStartTime}
        />
      )}

      <div
        className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 dark:bg-neutral-950/95 backdrop-blur border-t border-gray-200 dark:border-gray-800"
        style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      >
        <div className="mx-auto max-w-md px-4 py-3 flex flex-col gap-2">
          {isActive ? (
            <div className="grid grid-cols-2 gap-2">
              {!isOnBreak ? (
                <button
                  onClick={onPause}
                  className="border border-gray-200 dark:border-gray-600 px-4 py-3.5 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-neutral-800 active:bg-gray-100 dark:active:bg-neutral-800 transition"
                >
                  Pause
                </button>
              ) : (
                <button
                  onClick={onResume}
                  className="border border-yellow-400 text-yellow-700 dark:text-yellow-400 px-4 py-3.5 rounded-xl text-sm font-medium hover:bg-yellow-50 dark:hover:bg-yellow-900/20 active:bg-yellow-100 dark:active:bg-yellow-900/30 transition"
                >
                  Resume
                </button>
              )}
              <button
                onClick={onClockOut}
                className="bg-[#F97316] text-white px-4 py-3.5 rounded-xl text-sm font-semibold hover:bg-[#EA580C] active:bg-[#C2410C] transition"
              >
                Clock-out
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={onClockIn}
                className="w-full bg-[#2563EB] text-white px-4 py-4 rounded-xl text-sm font-semibold hover:bg-[#1d4ed8] active:bg-[#1d4ed8] transition"
              >
                Clock In
              </button>
              <button
                onClick={onClockInCustom}
                className="w-full flex items-center justify-center gap-1.5 py-2 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition"
              >
                <Clock3 className="h-3.5 w-3.5" />
                Clock in at specific time
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
